import { useEffect, useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { DataTable } from '@/components/ui/DataTable';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { PhoneNumberInput } from '@/components/ui/PhoneNumberInput';
import { Textarea } from '@/components/ui/Textarea';
import { useAuth } from '@/app/AuthProvider';
import {
  useCompanyContacts,
  useDeleteCompanyContact,
  useSaveCompanyContact,
  type CompanyContact,
} from '@/features/admin/hooks';
import { CONTACT_ROLE_SUGGESTIONS, friendlyAdminError } from '@/features/admin/model';
import { formatPhone } from '@/lib/formatters';
import { canWrite } from '@/lib/permissions';

export function ContactsPage() {
  const { role } = useAuth();
  const canEdit = canWrite(role, 'company_admin');
  const { data, isLoading } = useCompanyContacts();
  const [editing, setEditing] = useState<CompanyContact | null>(null);
  const [creating, setCreating] = useState(false);
  const rows = useMemo(() => data ?? [], [data]);

  const columns = useMemo<ColumnDef<CompanyContact>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        size: 200,
        cell: ({ row }) => <span className="font-medium text-foreground">{row.original.name}</span>,
      },
      {
        id: 'role',
        accessorFn: (row) => row.role ?? '',
        header: 'Role',
        size: 180,
        cell: ({ row }) => row.original.role || <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'company',
        accessorFn: (row) => row.company ?? '',
        header: 'Company',
        size: 180,
        cell: ({ row }) => row.original.company || <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'email',
        accessorFn: (row) => row.email ?? '',
        header: 'Email',
        size: 220,
        cell: ({ row }) =>
          row.original.email ? (
            <a
              href={`mailto:${row.original.email}`}
              className="text-info hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {row.original.email}
            </a>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'phone',
        accessorFn: (row) => formatPhone(row.phone),
        header: 'Phone',
        size: 150,
        cell: ({ row }) =>
          formatPhone(row.original.phone) || <span className="text-muted-foreground">—</span>,
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Admin"
        title="Key contacts"
        actions={
          canEdit ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              Add
            </Button>
          ) : undefined
        }
      />
      <Card>
        <CardContent className="pt-6">
          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(row) => row.id}
            onRowClick={(row) => setEditing(row)}
            searchPlaceholder="Search contacts…"
            tableId="admin-contacts"
            initialPageSize={25}
            emptyState="No outside contacts yet."
            mobileCard={{
              title: (row) => row.name,
              subtitle: (row) => [row.role, row.company].filter(Boolean).join(' · ') || '—',
              meta: (row) => [
                { label: 'Email', value: row.email || '—' },
                { label: 'Phone', value: formatPhone(row.phone) || '—' },
              ],
            }}
          />
        </CardContent>
      </Card>
      <ContactDialog
        open={creating || !!editing}
        contact={editing}
        canEdit={canEdit}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
          }
        }}
      />
    </div>
  );
}

function ContactDialog({
  open,
  contact,
  canEdit,
  onOpenChange,
}: {
  open: boolean;
  contact: CompanyContact | null;
  canEdit: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const save = useSaveCompanyContact();
  const remove = useDeleteCompanyContact();
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(contact?.name ?? '');
    setCompany(contact?.company ?? '');
    setRole(contact?.role ?? '');
    setEmail(contact?.email ?? '');
    setPhone(contact?.phone ?? '');
    setNotes(contact?.notes ?? '');
    setConfirmDelete(false);
  }, [open, contact]);

  const pending = save.isPending || remove.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!canEdit) return;
            void save
              .mutateAsync({ id: contact?.id, name, company, role, email, phone, notes })
              .then(() => {
                toast.success(contact ? 'Contact updated.' : 'Contact added.');
                onOpenChange(false);
              })
              .catch((err) => toast.error(friendlyAdminError(err, 'Could not save.')));
          }}
        >
          <DialogHeader>
            <DialogTitle>{contact ? contact.name : 'Add a contact'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">Name</Label>
              <Input
                id="contact-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={!canEdit}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-role">Role</Label>
              <Input
                id="contact-role"
                list="contact-role-suggestions"
                value={role}
                onChange={(event) => setRole(event.target.value)}
                disabled={!canEdit}
                placeholder="Attorney, broker, …"
              />
              <datalist id="contact-role-suggestions">
                {CONTACT_ROLE_SUGGESTIONS.map((suggestion) => (
                  <option key={suggestion} value={suggestion} />
                ))}
              </datalist>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="contact-company">Company</Label>
              <Input
                id="contact-company"
                value={company}
                onChange={(event) => setCompany(event.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone">Phone</Label>
              <PhoneNumberInput
                id="contact-phone"
                value={phone}
                onChange={setPhone}
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="contact-notes">Notes</Label>
              <Textarea
                id="contact-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                disabled={!canEdit}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {canEdit && contact ? (
              confirmDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={pending}
                  onClick={() =>
                    void remove
                      .mutateAsync(contact.id)
                      .then(() => {
                        toast.success('Contact deleted.');
                        onOpenChange(false);
                      })
                      .catch((err) => toast.error(friendlyAdminError(err, 'Could not delete.')))
                  }
                >
                  Confirm delete
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              )
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {canEdit && (
                <Button type="submit" disabled={pending || !name.trim()}>
                  Save
                </Button>
              )}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
